import * as React from "react";
import { useToastController } from "@fluentui/react-components";

import Notification from "../components/Notification";
import { ToasterContext } from "./toaster-context-api";
import { isUserSignedIn, getUserIfSignedIn } from "../../helpers/auth/auth";
import { CUSTOM_EVENTS, LOG_IN_METHODS } from "../../helpers/utils";
import { UserLoginInformation } from "../../helpers/auth/auth-utils";
import { publicClientApp } from "../../helpers/auth/fallbackauthdialog";

const defaultUser = {
  isAuthenticated: false,
  name: null,
  email: null,
  signedOutAccount: null,
  signInMethod: null,
};

export const UserContext = React.createContext(defaultUser);

export default function UserContextProvider({ children }) {
  const [ctx, setCtx] = React.useState(defaultUser);
  const toasterId = React.useContext(ToasterContext);
  const { dispatchToast } = useToastController(toasterId);

  const handleUserLogIn = async (event: Event) => {
    let user = (event as CustomEvent).detail;

    setCtx(user);
    dispatchToast(<Notification msg="You're signed in successfully" subtitle={`Signed in as ${user.email}`} />, {
      intent: "success",
    });

    // Cache user information
    UserLoginInformation.setUser(user);
    UserLoginInformation.setLogInMethod(user.signInMethod);
  };

  const handleUserLogOut = async (event: Event) => {
    const user = (event as CustomEvent).detail;

    if (user.signInMethod === LOG_IN_METHODS.MSAL) {
      await publicClientApp.logoutPopup({
        account: publicClientApp.getActiveAccount(),
      });
    }

    setCtx(user);

    dispatchToast(
      <Notification
        msg={`You're signed out successfully from ${user.signedOutAccount} account`}
        subtitle="You can sign in again at any time"
      />,
      { intent: "success" },
    );

    UserLoginInformation.clear();
  };

  React.useEffect(() => {
    window.addEventListener(CUSTOM_EVENTS.USER_LOG_IN, handleUserLogIn);
    window.addEventListener(CUSTOM_EVENTS.USER_LOG_OUT, handleUserLogOut);

    if (isUserSignedIn()) getUserIfSignedIn();
  }, []);

  return <UserContext.Provider value={ctx}>{children}</UserContext.Provider>;
}
